<template>
  <content-editor-modal class="question-editor-modal" :title="modalTitle" @close="closeModal">
    <template #header-actions>
      <div v-if="showNav" class="flex gap-2">
        <b-button size="is-small" icon-left="chevron-left" @click="requestNavigate(-1)" :disabled="!canPrev"/>
        <b-button size="is-small" icon-left="chevron-right" @click="requestNavigate(1)" :disabled="!canNext"/>
      </div>
    </template>

    <div v-if="isEditMode" class="question-editor-layout">
      <main class="question-editor-main">
        <h4 class="question-editor-section-title">Question 1</h4>
        <input
          v-model="safeQuestionData.title"
          type="text"
          class="question-editor-title-input"
          placeholder="Question"
          :class="{ 'question-editor-title-input--error': hasErrorSingle('title') }"
        />
        <p v-if="hasErrorSingle('title')" class="text-red-600 text-xs mt-1">{{ firstErrorSingle('title') }}</p>

        <div class="question-editor-response">
          <div
            class="question-editor-response-panel"
            :class="{ 'question-editor-response-panel--error': hasErrorSingle('response') }"
          >
            <quill-editor
              ref="myQuillEditor"
              v-model="safeQuestionData.response"
              :options="quillOptions"
            />
          </div>
          <p v-if="hasErrorSingle('response')" class="text-red-600 text-xs mt-1">{{ firstErrorSingle('response') }}</p>
        </div>

        <p class="question-editor-link-helper">
          <strong>Pour ajouter un lien vers une ressource :</strong>
          surlignez le mot sur lequel créer un lien, cliquez sur le bouton « Lien » puis renseignez l’URL au format :
          <code>/article/identifiant-unique</code>,
          <code>/question/identifiant-unique</code>,
          <code>/sondage/identifiant-unique</code>,
          <code>/document/identifiant-unique</code>,
          <code>/assistant</code> (ouvre l’assistant IA)
        </p>
      </main>

      <aside class="question-editor-sidebar">
        <t-input-group label="Type">
          <div :class="errorWrapSingle('type')">
            <t-select
              placeholder="Choisissez un type"
              :options="types"
              v-model="safeQuestionData.type.uuid"
              text-attribute="name"
              value-attribute="uuid"
            />
          </div>
          <p v-if="hasErrorSingle('type')" class="text-red-600 text-xs mt-1">{{ firstErrorSingle('type') }}</p>
        </t-input-group>

        <t-input-group label="Catégorie">
          <div :class="errorWrapSingle('category')">
            <t-select
              placeholder="Choisissez une Catégorie"
              :options="categories"
              v-model="safeQuestionData.category.uuid"
              value-attribute="uuid"
            />
          </div>
          <p v-if="hasErrorSingle('category')" class="text-red-600 text-xs mt-1">{{ firstErrorSingle('category') }}</p>
        </t-input-group>

        <section class="question-editor-sidebar-section">
          <div class="question-editor-panel-title">Documents</div>
          <file-pond
            name="questionDocuments"
            ref="pond"
            label-idle="<i class='fas fa-paperclip'></i> Ajouter des documents"
            allow-multiple
            :allow-paste="false"
            :files="questionFiles"
            @addfile="addFile"
            @removefile="deleteFile"
            credits=""
          />
        </section>

        <section class="question-editor-sidebar-section">
          <div class="question-editor-panel-title">Groupes</div>
          <t-rich-select
            v-model="safeQuestionData.groupUuids"
            multiple
            :close-on-select="false"
            :options="usersGroups"
            placeholder="Sélectionner..."
          />
        </section>

        <t-input-group label="Mots-clés">
          <vue-tags-input
            v-model="currentKeyword"
            :tags="questionKeywords"
            @tags-changed="updateTags"
            allow-edit-tags
            class="w-full max-w-none"
            add-from-paste
            :separators="[';', ',']"
            :add-on-key="[13, 188, ';']"
          />
        </t-input-group>

        <div class="question-editor-statuses">
          <label class="content-editor-modal__status-toggle">
            <t-toggle
              v-model="safeQuestionData.is_enabled"
              :value="true"
              :uncheckedValue="false"
              :checked="safeQuestionData.is_enabled || true"
            />
            <span>{{ safeQuestionData.is_enabled ? 'Activé' : 'Désactivé' }}</span>
          </label>
          <label
            v-if="shouldDisplayShareToggle"
            class="content-editor-modal__status-toggle"
          >
            <t-toggle
              v-model="safeQuestionData.is_shareable"
              :value="true"
              :uncheckedValue="false"
            />
            <span>Partage public</span>
          </label>
        </div>
      </aside>
    </div>

    <div v-else class="question-editor-bulk">
      <div
        v-for="(question, idx) in questions"
        :key="question.localId"
        class="question-editor-layout"
      >
        <main class="question-editor-main">
          <div class="question-editor-main-header">
            <h4 class="question-editor-section-title">Question {{ idx + 1 }}</h4>
            <button
              v-if="questions.length > 1"
              type="button"
              class="question-editor-remove-button"
              @click="removeQuestion(idx)"
            >
              Retirer
            </button>
          </div>
          <input
            v-model="question.title"
            type="text"
            class="question-editor-title-input"
            placeholder="Question"
            :class="{ 'question-editor-title-input--error': hasErrorBulk(question,'title') }"
          />
          <p v-if="hasErrorBulk(question,'title')" class="text-red-600 text-xs mt-1">{{ firstErrorBulk(question,'title') }}</p>

          <div class="question-editor-response">
            <div
              class="question-editor-response-panel"
              :class="{ 'question-editor-response-panel--error': hasErrorBulk(question,'response') }"
            >
              <quill-editor
                :ref="'bulkEditor-' + idx"
                v-model="question.response"
                :options="quillOptions"
              />
            </div>
            <p v-if="hasErrorBulk(question,'response')" class="text-red-600 text-xs mt-1">{{ firstErrorBulk(question,'response') }}</p>
          </div>

          <p class="question-editor-link-helper">
            <strong>Pour ajouter un lien vers une ressource :</strong>
            surlignez le mot sur lequel créer un lien, cliquez sur le bouton « Lien » puis renseignez l’URL au format :
            <code>/article/identifiant-unique</code>,
            <code>/question/identifiant-unique</code>,
            <code>/sondage/identifiant-unique</code>,
            <code>/document/identifiant-unique</code>,
            <code>/assistant</code> (ouvre l’assistant IA)
          </p>
        </main>

        <aside class="question-editor-sidebar">
          <t-input-group label="Type">
            <div :class="errorWrapBulk(question,'type')">
              <t-select
                placeholder="Choisissez un type"
                :options="types"
                v-model="question.type.uuid"
                text-attribute="name"
                value-attribute="uuid"
              />
            </div>
            <p v-if="hasErrorBulk(question,'type')" class="text-red-600 text-xs mt-1">{{ firstErrorBulk(question,'type') }}</p>
          </t-input-group>
          <t-input-group label="Catégorie">
            <div :class="errorWrapBulk(question,'category')">
              <t-select
                placeholder="Choisissez une Catégorie"
                :options="categories"
                v-model="question.category.uuid"
                value-attribute="uuid"
              />
            </div>
            <p v-if="hasErrorBulk(question,'category')" class="text-red-600 text-xs mt-1">{{ firstErrorBulk(question,'category') }}</p>
          </t-input-group>

          <section class="question-editor-sidebar-section">
            <div class="question-editor-panel-title">Documents</div>
            <file-pond
              name="questionDocuments"
              :key="`${question.localId}-pond`"
              label-idle="<i class='fas fa-paperclip'></i> Ajouter des documents"
              allow-multiple
              :allow-paste="false"
              @addfile="(error, file) => addBulkFile(idx, error, file)"
              @removefile="(error, file) => deleteBulkFile(idx, error, file)"
              credits=""
            />
          </section>

          <section class="question-editor-sidebar-section">
            <div class="question-editor-panel-title">Groupes</div>
            <t-rich-select
              v-model="question.groupUuids"
              multiple
              :close-on-select="false"
              :options="usersGroups"
              placeholder="Sélectionner..."
            />
          </section>

          <t-input-group label="Mots-clés">
            <vue-tags-input
              v-model="question._currentKeyword"
              :tags="question._keywordsTags"
              @tags-changed="tags => updateBulkTags(idx, tags)"
              allow-edit-tags
              class="w-full max-w-none"
              add-from-paste
              :separators="[';', ',']"
              :add-on-key="[13, 188, ';']"
            />
          </t-input-group>

          <div class="question-editor-statuses">
            <label class="content-editor-modal__status-toggle">
              <t-toggle v-model="question.is_enabled" :value="true" :uncheckedValue="false" :checked="question.is_enabled || true" />
              <span>{{ question.is_enabled ? 'Activé' : 'Désactivé' }}</span>
            </label>
            <label
              v-if="shouldDisplayShareToggle"
              class="content-editor-modal__status-toggle"
            >
              <t-toggle v-model="question.is_shareable" :value="true" :uncheckedValue="false" />
              <span>Partage public</span>
            </label>
          </div>
        </aside>
      </div>
      <div v-if="questions.length > 1 && !disableCreationNotification" class="question-editor-notification">
        <t-input-group
          label="Texte de notification"
          :variant="{ 'danger': bulkNotificationError }"
          :feedback="bulkNotificationError"
        >
          <t-input
            v-model="bulkNotificationText"
            type="text"
            :maxlength="bulkNotificationMaxLength"
            placeholder="Saisissez le texte de notification…"
          />
        </t-input-group>
        <p class="text-xs text-gray-500 mt-1">
          {{ bulkNotificationText.length }}/{{ bulkNotificationMaxLength }}
        </p>
      </div>
    </div>

    <template #footer>
      <div v-if="!isEditMode" class="content-editor-modal__footer-options">
        <button type="button" class="content-editor-modal__button" :disabled="savingBulk" @click="addQuestion">
          Ajouter une question
        </button>
        <label v-if="!disableCreationNotification" class="content-editor-modal__status-toggle">
          <t-toggle v-model="bulkToNotify" :value="true" :uncheckedValue="false" />
          <span>{{ bulkToNotify ? 'Notifier' : 'Ne pas notifier' }}</span>
        </label>
      </div>

      <div class="content-editor-modal__footer-actions question-editor-footer-actions">
        <button
          v-if="safeQuestionData.uuid"
          type="button"
          class="content-editor-modal__button content-editor-modal__button--danger"
          @click.prevent="deleteQuestion"
        >
          Supprimer
        </button>
        <b-tooltip
          v-if="isEditMode"
          :label="renotifyTooltip"
          :active="renotifyDisabled"
          position="is-top"
        >
          <button
            type="button"
            class="content-editor-modal__button"
            :disabled="renotifyDisabled"
            @click.prevent="notifyQuestion"
          >
            Renotifier
          </button>
        </b-tooltip>
        <button
          v-if="isEditMode"
          type="button"
          class="content-editor-modal__button content-editor-modal__button--primary"
          @click.prevent="saveQuestion"
        >
          Sauvegarder
        </button>
        <button
          v-else
          type="button"
          class="content-editor-modal__button content-editor-modal__button--primary"
          :disabled="savingBulk || questions.length === 0"
          @click.prevent="saveBulk"
        >
          {{ savingBulk ? 'Enregistrement...' : 'Sauvegarder' }}
        </button>
      </div>
    </template>
  </content-editor-modal>
</template>

<script>
/* eslint-disable */
import 'quill/dist/quill.core.css'
import 'quill/dist/quill.snow.css'
import VueTagsInput from '@johmun/vue-tags-input';
import { quillEditor } from 'vue-quill-editor'

import vueFilePond from 'vue-filepond';
import FilePondPluginFileValidateType from 'filepond-plugin-file-validate-type';
import FilePondPluginImagePreview from 'filepond-plugin-image-preview';
import FilePondPluginImageCrop from 'filepond-plugin-image-crop';

const FilePond = vueFilePond(
  FilePondPluginFileValidateType,
  FilePondPluginImagePreview,
  FilePondPluginImageCrop
);


import { http } from '@/services/api.service'
import { format } from 'date-fns'
import French from 'vue-tailwind/dist/l10n/fr'

import PageHeader from '@/components/PageHeader'
import PageContent from '@/components/PageContent.vue'
import ContentEditorModal from '@/components/content/ContentEditorModal.vue'
import { formatWithOptions } from 'date-fns/fp';
import faqAccordionQuill from '@/mixins/faqAccordionQuill'
import { normalizeInternalContentLinks } from '@/utils/internalContentLinks'
import { renotificationState } from '@/utils/faqRenotification'

export default {
  name: 'mvp-questions-form',
  components: {
    PageHeader,
    PageContent,
    quillEditor,
    FilePond,
    VueTagsInput,
    ContentEditorModal
  },
  mixins: [faqAccordionQuill],
  data () {
    return {
      questionData: {
        uuid: null,
        title: '',
        response: '',
        type: {
          uuid: null
        },
        category: {
          uuid: null
        },
        is_enabled: true,
        keywords: [],
        groupUuids: [],
        files: [],
        to_notify: true,
        is_shareable: false
      },
  questions: [],
  savingBulk: false,
  validationErrors: {},
  bulkValidationErrors: {},
      questionFiles: [],
      currentKeyword: '',
      questionKeywords: [],
      thumbnailFile: null,
      thumbnailUrl: null,
      document_file: null,
      thumbnail: null,
      files: [],
      storageUrl: process.env.VUE_APP_STORAGE_URL,
      filesToAdd: [],
      locale: French,
      categories: [],
      types: [],
      usersGroups: [],
      bulkToNotify: true,
      bulkNotificationText: '',
      bulkNotificationError: '',
      bulkNotificationMaxLength: 180,
      isDirty: false,
      originalSnapshot: null
    }
  },
  props: [
    'question',
    'questionKey',
    'orderedUuids',
    'currentIndex',
    'disableCreationNotification'
  ],
  mounted () {
    this.getCategories()
    this.getTypes()
    this.getGroups()
    this.getQuestion()
    if (this.isEditMode) {
      this.loadQuestionGroups(this.safeQuestionData.uuid)
    }
    if (!this.isEditMode && this.questions.length === 0) {
      this.addQuestion()
    }
  },
  beforeMount: async function () {
    if (this.$route.params.uuid) {
      await http.get(`/faq/${this.$route.params.uuid}`).then(res => {
        this.questionData = res.data
        this.questionData.is_shareable = !!res.data.is_shareable
        this.questionData.groupUuids = this.extractNotificationGroupUuids(res.data)
        this.questionUniqueKey = res.data.uuid
        console.log('question', this.question)
      }).catch(err => {
        let message
        if (err && err.response.status === 404) {
          message = 'La question demandée est introuvable'
        } else {
          message = 'Erreur de chargement de la question'
        }

        this.$buefy.notification.open({
          duration: 3000,
          message,
          position: 'is-bottom-left',
          type: 'is-warning',
          hasIcon: true,
          icon: 'server'
        })
      })
    } else {
      this.questionUniqueKey = this.questionKey;
      this.questionData = this.question;
      if (this.questionData) {
        this.questionData.is_shareable = !!this.questionData.is_shareable
        this.$set(this.questionData, 'groupUuids', [])
      }
    }
    if ( ! this.question) {
      this.questionData.category = {
        uuid: null
      }
      this.questionData.type = {
        uuid: null
      }
    }
  },
  computed: {
    modalTitle () {
      if (this.isEditMode) return 'Modifier la FAQ'
      return this.questions.length > 1 ? 'Créer plusieurs FAQ' : 'Créer une FAQ'
    },
    domain () {
      return this.$store.getters.domain
    },
    isEditMode () {
      return !!this.safeQuestionData.uuid
    },
    isTractApp () {
      return process.env.VUE_APP_APPLICATION === 'tract'
    },
    shouldDisplayShareToggle () {
      return this.isTractApp
    },
    shouldNotifyOnCreate () {
      return !this.disableCreationNotification && this.bulkToNotify
    },
    safeQuestionData () {
      return this.questionData || {
        uuid: null,
        title: '',
        response: '',
        type: { uuid: null },
        category: { uuid: null },
        is_enabled: true,
        is_shareable: false,
        keywords: [],
        groupUuids: [],
        files: [],
        to_notify: true
      }
    },
    showNav () {
      return this.isEditMode && Array.isArray(this.orderedUuids) && this.orderedUuids.length > 1
    },
    canPrev () {
      return this.showNav && this.currentIndex > 0
    },
    canNext () {
      return this.showNav && this.currentIndex < (this.orderedUuids.length - 1)
    },
    renotificationCooldownReason () {
      if (!this.safeQuestionData.is_enabled) return ''
      return renotificationState(this.safeQuestionData).blockedReason
    },
    renotifyDisabled () {
      return !this.safeQuestionData.is_enabled || this.isDirty || Boolean(this.renotificationCooldownReason)
    },
    renotifyTooltip () {
      if (!this.safeQuestionData.is_enabled) {
        return 'Activez puis enregistrez la FAQ avant de la renotifier.'
      }
      if (this.isDirty) {
        return 'Enregistrez vos modifications avant de renotifier.'
      }
      return this.renotificationCooldownReason
    }
  },
  watch: {
    questionKey () {
      // reset dirty flag when switching to another question
      this.isDirty = false
    },
    question (val) {
      // update internal model when parent changes current question
      this.questionData = val || {}
      // reset files preview buffer
      this.questionFiles = []
      this.getQuestion()
      if (val && val.uuid) {
        this.loadQuestionGroups(val.uuid)
      }
    },
    questionData: {
      deep: true,
      handler () {
        // continuously reflect whether the form differs from the initial snapshot
        this.isDirty = this.checkDirty()
      }
    },
    questions: {
      deep: true,
      handler () {
        if (this.questions.length <= 1) {
          this.bulkNotificationError = ''
        }
      }
    }
  },
  methods: {
    closeModal () {
      if (this.$parent && typeof this.$parent.hide === 'function') {
        this.$parent.hide()
        return
      }
      if (this.$parent && this.$parent.$modal && this.$parent.$modal.hide) {
        this.$parent.$modal.hide('question-form')
      }
    },
    baseQuestion () {
      return {
        localId: Math.random().toString(36).substr(2, 9),
        title: '',
        response: '',
        type: { uuid: null },
        category: { uuid: null },
        is_enabled: true,
        is_shareable: false,
        keywords: [],
        groupUuids: [],
        to_notify: this.shouldNotifyOnCreate,
        _currentKeyword: '',
        _keywordsTags: [],
        _files: []
      }
    },
    addQuestion () {
      this.questions.push(this.baseQuestion())
    },
    resetErrors () {
      this.validationErrors = {}
      this.bulkValidationErrors = {}
      this.bulkNotificationError = ''
    },
    hasErrorSingle (field) {
      return !!(this.validationErrors[field] && this.validationErrors[field].length)
    },
    firstErrorSingle (field) {
      return this.hasErrorSingle(field) ? this.validationErrors[field][0] : ''
    },
    hasErrorBulk (question, field) {
      const errs = this.bulkValidationErrors[question.localId]
      return !!(errs && errs[field] && errs[field].length)
    },
    firstErrorBulk (question, field) {
      return this.hasErrorBulk(question, field) ? this.bulkValidationErrors[question.localId][field][0] : ''
    },
    setSingleErrors (errors) {
      this.validationErrors = errors || {}
    },
    setBulkErrors (index, errors) {
      const q = this.questions[index]
      if (!q) return
      this.$set(this.bulkValidationErrors, q.localId, errors || {})
    },
    errorWrapSingle (field) {
      return this.hasErrorSingle(field) ? 'border border-red-500 rounded' : ''
    },
    errorWrapBulk (question, field) {
      return this.hasErrorBulk(question, field) ? 'border border-red-500 rounded' : ''
    },
    clientValidateSingle () {
      const errs = {}
      if (!this.safeQuestionData.title) errs.title = ['Titre requis']
      if (!this.safeQuestionData.response) errs.response = ['Réponse requise']
      if (!this.safeQuestionData.category || !this.safeQuestionData.category.uuid) errs.category = ['Catégorie requise']
      if (!this.safeQuestionData.type || !this.safeQuestionData.type.uuid) errs.type = ['Type requis']
      this.validationErrors = errs
      return Object.keys(errs).length === 0
    },
    clientValidateBulk () {
      let allValid = true
      this.bulkValidationErrors = {}
      this.questions.forEach((q, idx) => {
        const errs = {}
        if (!q.title) errs.title = ['Titre requis']
        if (!q.response) errs.response = ['Réponse requise']
        if (!q.category || !q.category.uuid) errs.category = ['Catégorie requise']
        if (!q.type || !q.type.uuid) errs.type = ['Type requis']
        if (Object.keys(errs).length) {
          allValid = false
          this.setBulkErrors(idx, errs)
        }
      })
      return allValid
    },
    removeQuestion (idx) {
      this.questions.splice(idx, 1)
    },
    updateBulkTags (idx, tags) {
      const q = this.questions[idx]
      q.keywords = tags.map(t => t.text)
      q._keywordsTags = tags
    },
    getQuestion () {
      this.questionKeywords = []
      if (this.questionData.keywords) {
        this.questionData.keywords.forEach(keyword => {
          this.questionKeywords.push({
            text: keyword
          })
        })
      }
      if (this.questionData.files) {
        this.questionData.files.forEach(file => {
          this.questionFiles.push({
            source: file.url,
            options: {
              type: 'local',
              file: {
                name: file.name,
                size: file.size
              },
              metadata: {
                uuid: file.uuid
              }
            }
          })
        })
      }
      // initializing done; reset dirty flag and store snapshot.
      // Use nextTick to let Quill/tags inputs normalize value before snapshot.
      this.isDirty = false
      this.$nextTick(() => {
        setTimeout(() => {
          this.originalSnapshot = this.snapshotQuestion()
          this.isDirty = false
        }, 0)
      })
    },
    getCategories () {
      http.get('/faq/category').then(({ data }) => {
        this.categories = data
      }, err => {
        console.log(err)
      })
    },
    getTypes () {
      http.get('/faq/type').then(({ data }) => {
        this.types = data
      }).catch(error => {
        console.log(error)
      })
    },
    getGroups () {
      return http.get('/groups').then(({ data }) => {
        this.usersGroups = (data || []).map(group => ({
          value: group.uuid,
          text: group.label
        }))
      }).catch(error => {
        console.log(error)
        this.usersGroups = []
      })
    },
    extractNotificationGroupUuids (question) {
      const notifications = question && Array.isArray(question.notifications)
        ? question.notifications
        : []
      return notifications
        .map(notification => notification && notification.group && notification.group.uuid)
        .filter(Boolean)
    },
    loadQuestionGroups (questionUuid) {
      if (!questionUuid) return Promise.resolve()
      return http.get(`/faq/${questionUuid}`).then(({ data }) => {
        if (!this.questionData || this.questionData.uuid !== questionUuid) return
        this.$set(this.questionData, 'groupUuids', this.extractNotificationGroupUuids(data))
        this.$nextTick(() => {
          this.originalSnapshot = this.snapshotQuestion()
          this.isDirty = false
        })
      }).catch(error => {
        console.log(error)
      })
    },
    parser (date) {
      return parse(date)
    },
    async requestNavigate (direction) {
      if (!direction || (!this.canPrev && direction < 0) || (!this.canNext && direction > 0)) return
      const hasChanges = this.isEditMode && this.checkDirty()
      if (hasChanges) {
        this.$buefy.dialog.confirm({
          title: 'Enregistrer les modifications ? ',
          message: 'Voulez-vous enregistrer vos modifications avant de naviguer ?',
          cancelText: 'Ne pas sauvegarder',
          confirmText: 'Sauvegarder',
          type: 'is-success',
          onConfirm: async () => {
            const ok = await this.saveQuestionKeepOpen()
            if (ok) {
              this.isDirty = false
              this.$emit('navigate', { direction })
            }
          },
          onCancel: () => {
            this.isDirty = false
            this.$emit('navigate', { direction })
          }
        })
        return
      }
      this.$emit('navigate', { direction })
    },
    snapshotQuestion () {
      const q = this.questionData || {}
      return {
        title: q.title || '',
        response: q.response || '',
        is_enabled: !!q.is_enabled,
        is_shareable: !!q.is_shareable,
        type_uuid: q.type && q.type.uuid ? q.type.uuid : null,
        category_uuid: q.category && q.category.uuid ? q.category.uuid : null,
        keywords: Array.isArray(q.keywords) ? [...q.keywords] : [],
        groupUuids: Array.isArray(q.groupUuids) ? [...q.groupUuids].sort() : []
      }
    },
    checkDirty () {
      const a = this.originalSnapshot || {}
      const b = this.snapshotQuestion()
      if (a.title !== b.title) return true
      if (a.response !== b.response) return true
      if (a.is_enabled !== b.is_enabled) return true
      if (a.is_shareable !== b.is_shareable) return true
      if (a.type_uuid !== b.type_uuid) return true
      if (a.category_uuid !== b.category_uuid) return true
      const ak = Array.isArray(a.keywords) ? a.keywords : []
      const bk = Array.isArray(b.keywords) ? b.keywords : []
      if (ak.length !== bk.length) return true
      for (let i = 0; i < ak.length; i++) {
        if (ak[i] !== bk[i]) return true
      }
      const ag = Array.isArray(a.groupUuids) ? a.groupUuids : []
      const bg = Array.isArray(b.groupUuids) ? b.groupUuids : []
      if (ag.length !== bg.length) return true
      for (let i = 0; i < ag.length; i++) {
        if (ag[i] !== bg[i]) return true
      }
      return false
    },
    appendGroupUuids (formData) {
      formData.append('sync_groups', '1')
      const groupUuids = Array.isArray(this.questionData.groupUuids)
        ? this.questionData.groupUuids
        : []
      groupUuids.forEach(groupUuid => {
        formData.append('groupUuids[]', groupUuid)
      })
    },
    async saveQuestionKeepOpen () {
      this.resetErrors()
      if (!this.clientValidateSingle()) {
        this.$notify({ group: 'maviepro-error', text: 'Veuillez corriger les erreurs.' })
        return false
      }
      const formData = new FormData()
      const isUpdate = !!this.questionData.uuid

      for (const prop in this.questionData) {
        if (prop !== 'type' && prop !== 'category' && prop !== 'files' && prop !== 'keywords' && prop !== 'groupUuids' && prop !== 'notifications') {
          const value = prop === 'response'
            ? normalizeInternalContentLinks(this.questionData[prop])
            : this.questionData[prop]
          formData.append(prop, value)
        }
      }

      if (this.questionData.keywords && this.questionData.keywords.length) {
        this.questionData.keywords.forEach(keyword => {
          formData.append('keywords[]', keyword)
        })
      }

      if (!formData.get('is_enabled')) {
        formData.append('is_enabled', this.questionData.is_enabled ? 1 : 0)
      }

      formData.delete('is_shareable')
      formData.append('is_shareable', this.questionData.is_shareable ? 1 : 0)

      if (this.questionData.type && this.questionData.type.uuid) {
        formData.append('type[uuid]', this.questionData.type.uuid)
      }

      if (this.questionData && this.questionData.to_notify !== undefined) {
        formData.delete('to_notify')
        formData.append('to_notify', this.questionData.to_notify ? 1 : 0)
      }

      if (this.questionData.category && this.questionData.category.uuid) {
        formData.append('category[uuid]', this.questionData.category.uuid)
      }

      this.appendGroupUuids(formData)

      const postUrl = '/faq' + (isUpdate ? `/${this.questionData.uuid}` : '')

      try {
        if (!isUpdate) {
          if (this.thumbnail) {
            formData.append('thumbnail', this.thumbnail)
          }
          this.files.map(file => {
            formData.append('files[]', file)
          })
        }
        await http.post(postUrl, formData)
        this.$emit('refresh')
        this.$notify({
          group: 'maviepro-success',
          text: isUpdate ? 'Question mise à jour.' : 'Question enregistrée.'
        })
        // update snapshot to current state after save
        this.originalSnapshot = this.snapshotQuestion()
        return true
      } catch (err) {
        let msg = isUpdate ? 'Erreur lors de la mise à jour.' : 'Erreur lors de la sauvegarde.'
        if (err && err.response && err.response.status === 422) {
          const data = err.response.data
          const errors = (data && (data.errors || (data.data && data.data.errors))) || {}
          this.setSingleErrors(errors)
          msg = 'Certains champs sont invalides.'
        } else if (err && err.response && err.response.data) {
          msg = err.response.data
        }
        this.$notify({ group: 'maviepro-error', text: msg })
        return false
      }
    },
    saveQuestion () {
  this.resetErrors()
      if (!this.clientValidateSingle()) {
        this.$notify({ group: 'maviepro-error', text: 'Veuillez corriger les erreurs.' })
        return
      }
      const formData = new FormData()
      const isUpdate = !!this.questionData.uuid
      console.log('isUpdate', isUpdate)
      
      for (const prop in this.questionData) {
        if (prop !== 'type' && prop !== 'category' && prop !== 'files' && prop !== 'keywords' && prop !== 'groupUuids' && prop !== 'notifications') {
          const value = prop === 'response'
            ? normalizeInternalContentLinks(this.questionData[prop])
            : this.questionData[prop]
          formData.append(prop, value)
        }
      }
      
      if (this.questionData.keywords && this.questionData.keywords.length) {
        this.questionData.keywords.forEach(keyword => {
          formData.append('keywords[]', keyword)
        })
      }

      if (!formData.get('is_enabled')) {
        formData.append('is_enabled', this.questionData.is_enabled ? 1 : 0)
      }

      formData.delete('is_shareable')
      formData.append('is_shareable', this.questionData.is_shareable ? 1 : 0)

      if (this.questionData.type && this.questionData.type.uuid) {
        formData.append('type[uuid]', this.questionData.type.uuid)
      }

      if (this.questionData && this.questionData.to_notify !== undefined) {
        formData.delete('to_notify')
        formData.append('to_notify', this.questionData.to_notify ? 1 : 0)
      }
      
      if (this.questionData.category && this.questionData.category.uuid) {
        formData.append('category[uuid]', this.questionData.category.uuid)
      }

      this.appendGroupUuids(formData)

      const postUrl = '/faq' + (isUpdate ? `/${this.questionData.uuid}` : '')

      if (!isUpdate) { // creation only, upload file
        if (this.thumbnail) {
          formData.append('thumbnail', this.thumbnail)
        }
        this.files.map(file => {
          formData.append('files[]', file)
        })

        http.post(postUrl, formData).then(res => {
          this.$emit('refresh')
          this.$parent.$modal.hide('question-form')
          this.$notify({
            group: 'maviepro-success',
            text: 'Question enregistrée.'
          })
        }).catch(err => {
          let msg = 'Erreur lors de la sauvegarde.'
          if (err && err.response && err.response.status === 422) {
            const data = err.response.data
            const errors = (data && (data.errors || (data.data && data.data.errors))) || {}
            this.setSingleErrors(errors)
            msg = 'Certains champs sont invalides.'
          } else if (err && err.response && err.response.data) {
            msg = err.response.data
          }
          this.$notify({ group: 'maviepro-error', text: msg })
        })
      } else {
        http.post(postUrl, formData).then(res => {
          this.$emit('refresh')
          this.$parent.$modal.hide('question-form')
          this.$notify({
            group: 'maviepro-success',
            text: 'Question mise à jour.'
          })
        }).catch(err => {
          console.log(err)
          let msg = 'Erreur lors de la mise à jour.'
          if (err && err.response && err.response.status === 422) {
            const data = err.response.data
            const errors = (data && (data.errors || (data.data && data.data.errors))) || {}
            this.setSingleErrors(errors)
            msg = 'Certains champs sont invalides.'
          } else if (err && err.response && err.response.data) {
            msg = err.response.data
          }
          this.$notify({ group: 'maviepro-error', text: msg })
        })
      }
    },
    async saveBulk () {
      if (this.savingBulk) return
      this.savingBulk = true
      this.resetErrors()
      this.bulkNotificationError = ''
      if (!this.clientValidateBulk()) {
        this.savingBulk = false
        this.$notify({ group: 'maviepro-error', text: 'Veuillez corriger les erreurs.' })
        return
      }
      if (this.questions.length > 1 && this.shouldNotifyOnCreate) {
        const trimmed = (this.bulkNotificationText || '').trim()
        if (!trimmed) {
          this.bulkNotificationError = 'Texte de notification requis.'
          this.savingBulk = false
          return
        }
        if (trimmed.length > this.bulkNotificationMaxLength) {
          this.bulkNotificationError = `Texte trop long (max ${this.bulkNotificationMaxLength} caractères).`
          this.savingBulk = false
          return
        }
      }
      const trimmedNotification = (this.bulkNotificationText || '').trim()
      const payload = {
        to_notify: this.shouldNotifyOnCreate ? 1 : 0,
        notification_text: this.questions.length > 1 && this.shouldNotifyOnCreate ? trimmedNotification : '',
        questions: this.questions.map(q => ({
          title: q.title,
          response: normalizeInternalContentLinks(q.response),
          keywords: q.keywords,
          is_enabled: q.is_enabled ? 1 : 0,
          is_shareable: q.is_shareable ? 1 : 0,
          groupUuids: Array.isArray(q.groupUuids) ? q.groupUuids : [],
          type: q.type.uuid ? { uuid: q.type.uuid } : undefined,
          category: q.category.uuid ? { uuid: q.category.uuid } : undefined
        }))
      }
      try {
        const res = await http.post('/faq/bulk', payload)
        const body = res?.data
        const candidates = [body, body?.data, body?.results]
        const created = candidates.find(item => Array.isArray(item)) || []
        const uploads = []
        created.forEach((createdQ, idx) => {
          const q = this.questions[idx]
          const files = (q && Array.isArray(q._files)) ? q._files : []
          if (!createdQ || !createdQ.uuid || files.length === 0) return
          const fd = new FormData()
          files.forEach(f => fd.append('files[]', f))
          uploads.push(
            http.post(`/faq/${createdQ.uuid}/file`, fd).catch(err => {
              console.error('File upload failed for question', createdQ.uuid, err)
            })
          )
        })
        if (uploads.length) {
          await Promise.all(uploads)
        }
        this.$emit('refresh')
        this.$parent.$modal.hide('question-form')
        this.$notify({ group: 'maviepro-success', text: 'Questions enregistrées.' })
      } catch (e) {
        let msg = 'Erreur lors de la sauvegarde multiple.'
        if (e && e.response && e.response.status === 422) {
          const data = e.response.data
          const errors = (data && (data.errors || (data.data && data.data.errors))) || {}
          const message = data.message || data.error || ''
          const match = /index\s+(\d+)/i.exec(message)
          if (match) {
            const idx = parseInt(match[1])
            this.setBulkErrors(idx, errors)
          } else {
            this.setBulkErrors(0, errors)
          }
          msg = 'Certains champs sont invalides.'
        }
        this.$notify({ group: 'maviepro-error', text: msg })
      } finally {
        this.savingBulk = false
      }
    },
    deleteQuestion () {
      this.$dialog.confirm({
        title: 'Supprimer cette question ?',
        text: 'Cette action ne peut pas être annulée.',
        icon: 'warning',
        cancelButtonText: 'Annuler',
        okButtonText: 'Supprimer'
      }).then((result) => {
        if (result.isOk && result.hideReason === 'ok') {
          http.delete(`/faq/${this.questionUniqueKey}`).then(res => {
            this.$notify({
              group: 'maviepro-success',
              text: 'Question supprimée.'
            })
            this.$emit('refresh')
            this.$parent.$modal.hide('question-form')
          }).catch(err => {
            console.log(err)
            this.$notify({
              group: 'maviepro-error',
              text: 'Problème lors de la suppression, veuillez contacter un administrateur.'
            })
          })
        }
      })
    },
    notifyQuestion () {
      if (!this.safeQuestionData.uuid) {
        this.$notify({
          group: 'maviepro-error',
          text: 'Impossible de renotifier une question non sauvegardée.'
        })
        return
      }

      this.$dialog.confirm({
        title: 'Notification',
        text: 'Êtes vous sur de vouloir renotifier cette question ?',
        icon: 'warning',
        cancelButtonText: 'Annuler',
        okButtonText: 'Renotifier'
      }).then((result) => {
        if (result.isOk && result.hideReason === 'ok') {
          http.post(`/faq/${this.safeQuestionData.uuid}/notify`).then(() => {
            this.$notify({
              group: 'maviepro-success',
              text: 'Notification envoyée.'
            })
            this.$emit('refresh')
            this.$parent.$modal.hide('question-form')
          }).catch(err => {
            console.log(err)
            const message = err && err.response && err.response.data && err.response.data.message
              ? err.response.data.message
              : 'Erreur lors de l\'envoi de la notification.'
            this.$notify({
              group: 'maviepro-error',
              text: message
            })
          })
        }
      })
    },
    deleteFile (error, file) {
      if (!this.questionData.uuid) {
        if (file && file.file) {
          this.files = this.files.filter(f => f !== file.file)
        }
        return false
      }
      const fileUuid = file.getMetadata('uuid')
      if (!fileUuid) return false
      http
        .delete(`/faq/${this.questionData.uuid}/file/${fileUuid}`)
        .then(({ data }) => {
          this.questionData.files = this.questionData.files.filter(doc => doc.uuid !== fileUuid)
        }).catch(err => {
          console.log(err)
        })
    },
    addFile (error, file) {
      if (file && file.file) {
        this.files.push(file.file)
      }
      // creation, fill only
      if ( ! this.questionData.uuid) {
        return false
      }
      if (error) return false
      // update: upload file now
      const formData = new FormData()
      if (file && file.file) {
        formData.append('files[]', file.file)
      }
      http.post(`/faq/${this.questionData.uuid}/file`, formData).then(({ data }) => {
        this.questionData.files = [...this.questionData.files, ...data]
        this.filesToAdd = []
      }).catch(err => {
        this.filesToAdd = []
        console.log(err)
      })
    },
    addBulkFile (idx, error, file) {
      const q = this.questions[idx]
      if (!q) return false
      if (error) return false
      if (file && file.file) {
        if (!Array.isArray(q._files)) this.$set(q, '_files', [])
        q._files.push(file.file)
      }
      return true
    },
    deleteBulkFile (idx, error, file) {
      const q = this.questions[idx]
      if (!q) return false
      if (file && file.file && Array.isArray(q._files)) {
        q._files = q._files.filter(f => f !== file.file)
      }
      return true
    },
    updateTags (tags) {
      this.questionData.keywords = tags.map(tag => tag.text)
    }
  }
}
</script>

<style lang="scss" scoped>

.question-editor-modal {
  max-width: 1120px;
}

.question-editor-layout {
  display: grid;
  grid-template-columns: minmax(0, 1.35fr) minmax(320px, 0.85fr);
  gap: 0;
  align-items: stretch;
}

.question-editor-main,
.question-editor-sidebar,
.question-editor-notification {
  min-width: 0;
}

.question-editor-main {
  display: flex;
  flex-direction: column;
  padding-right: 2.5rem;
}

.question-editor-sidebar {
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
  padding-left: 2.5rem;
  border-left: 1px solid #e5e7eb;
}

.question-editor-main-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
}

.question-editor-section-title {
  margin: 0 0 1.25rem;
  color: #171717;
  font-size: 1.05rem;
  font-weight: 600;
  line-height: 1.25;
}

.question-editor-main-header .question-editor-section-title {
  margin-bottom: 1.25rem;
}

.question-editor-remove-button {
  margin: 0 0 1.25rem;
  padding: 0.35rem 0.7rem;
  color: #b91c1c;
  background: #fff;
  border: 1px solid #fecaca;
  border-radius: 0.375rem;
  font-size: 0.8rem;
  font-weight: 600;
}

.question-editor-title-input {
  display: block;
  width: 100%;
  height: 3.25rem;
  padding: 0 1rem;
  color: #6b7280;
  background: #fff;
  border: 1px solid #dbe1ea;
  border-radius: 0.5rem;
  font-size: 1rem;
  font-weight: 600;
  outline: none;
}

.question-editor-title-input::placeholder {
  color: #c7ccd3;
  font-size: 1rem;
}

.question-editor-title-input:focus {
  border-color: #445877;
  box-shadow: 0 0 0 3px rgba(68, 88, 119, 0.14);
}

.question-editor-title-input--error,
.question-editor-title-input--error:focus {
  border-color: #ef4444;
}

.question-editor-response-panel {
  height: clamp(290px, 42vh, 430px);
  margin-top: 0.9rem;
  overflow: hidden;
  background: #fff;
  border: 1px solid #dbe1ea;
  border-radius: 0.5rem;
}

.question-editor-response-panel--error {
  border-color: #ef4444;
}

.question-editor-response-panel ::v-deep .quill-editor {
  display: flex;
  flex-direction: column;
  height: 100%;
}

.question-editor-response-panel ::v-deep .ql-toolbar.ql-snow {
  flex: 0 0 auto;
  padding: 0.9rem 1rem;
  border: 0;
  border-bottom: 1px solid #dbe1ea;
}

.question-editor-response-panel ::v-deep .ql-container.ql-snow {
  flex: 1 1 auto;
  min-height: 0;
  border: 0;
  color: #6b7280;
  font-size: 1rem;
}

.question-editor-response-panel ::v-deep .ql-editor {
  min-height: 0;
  height: 100%;
  padding: 1.4rem 1rem;
}

.question-editor-response-panel ::v-deep .ql-editor.ql-blank::before {
  color: #c7ccd3;
  font-size: 1rem;
  font-style: normal;
  font-weight: 600;
}

.question-editor-link-helper {
  margin: 0.65rem 0 0;
  color: #6b7280;
  font-size: 0.8rem;
  font-weight: 500;
  line-height: 1.4;
}

.question-editor-link-helper strong {
  color: #374151;
  font-weight: 700;
}

.question-editor-link-helper code {
  padding: 0.05rem 0.25rem;
  color: #445877;
  background: #eef2f7;
  border-radius: 0.25rem;
  font-size: 0.75rem;
  white-space: nowrap;
}

.question-editor-sidebar-section {
  min-width: 0;
}

.question-editor-panel-title {
  margin-bottom: 0.8rem;
  color: #222;
  font-size: 0.78rem;
  font-weight: 700;
  letter-spacing: 0.02em;
  line-height: 1.2;
  text-transform: uppercase;
}

.question-editor-sidebar ::v-deep .filepond--root {
  margin-bottom: 0;
}

.question-editor-sidebar ::v-deep .filepond--drop-label {
  min-height: 2.75rem;
  color: #6b7280;
  font-size: 0.78rem;
  font-weight: 600;
}

.question-editor-sidebar ::v-deep .filepond--drop-label label {
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
}

.question-editor-sidebar ::v-deep .filepond--panel-root {
  background: #fbfcfe;
  border: 1px dashed #cbd5e1;
  border-radius: 0.55rem;
}

.question-editor-statuses {
  display: flex;
  flex-direction: column;
  gap: 0.85rem;
  margin-top: auto;
  padding-top: 0.5rem;
}

.question-editor-bulk {
  display: flex;
  flex-direction: column;
}

.question-editor-bulk .question-editor-layout {
  padding-bottom: 2.5rem;
}

.question-editor-bulk .question-editor-layout + .question-editor-layout {
  padding-top: 2.5rem;
  border-top: 1px solid #e5e7eb;
}

.question-editor-notification {
  padding: 1.25rem;
  background: #fff;
  border: 1px solid #dbe1ea;
  border-radius: 0.5rem;
}

.question-editor-footer-actions {
  margin-left: auto;
}

@media (max-width: 860px) {
  .question-editor-layout {
    grid-template-columns: minmax(0, 1fr);
  }

  .question-editor-sidebar {
    margin-top: 1.5rem;
    padding: 1.5rem 0 0;
    border-top: 1px solid #e5e7eb;
    border-left: 0;
  }

  .question-editor-main {
    padding-right: 0;
  }

  .question-editor-link-helper code {
    white-space: normal;
    overflow-wrap: anywhere;
  }
}

.thumbnail-preview {
  width: 128px;
  height: 128px;
  box-shadow: 1px 2px 5px 0 #999;
  border-radius: 2px;

}

.file-name {
  border: 1px solid #999 !important;
}

.documents-list li .columns {
  display: flex;
  align-items: center;

  a:hover,
  .remove:hover {
    cursor: pointer;
  }
}

.thumbnail .field-body .img {
  position: relative;
  margin-top: 0.75rem;
  margin-bottom: 0.75rem;

  .remove {
    position: absolute;
    top: -0.75rem;
    right: -0.75rem;
    cursor: pointer;
  }
}
</style>
